import { NavLink, useLocation, useNavigate } from "react-router-dom"
import { TopBar } from "./TopBar"
import Cookies from "js-cookie";
import useAuth from "../hook/useAuth";
import { useEffect, useRef, useState } from "react";
import { baseURL } from "../config/api";
import { jwtDecode } from "jwt-decode";
import useAccessControl from "../hook/useAccessControl";
import { appAdminSettingRoutes } from "../routes/adminSettingsRoutes";

export const NavBar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { auth, setAuth }: any = useAuth();
    const { permissions }: any = useAccessControl();
    const token = Cookies.get("token-express-tire");
    const profileImg = Cookies.get("profileImg-express-tire");
    const role = Cookies.get("role-express-tire") || "";
    const isAdmin = role.toLowerCase() === "admin";
    const [authIsFound, setAuthIsFound] = useState<boolean>(false);
    const [hideMenuDropDown, setHideMenuDropDown] = useState<boolean>(false);
    const dropDownVisibleRef = useRef<HTMLDivElement | null>(null);
    const hideHeaderForBookingConfirm = "/booking-confirm";
    const hideHeaderForEmailView = "/admin/emaillogs/view";
    const hideHeaderForAdmin = "admin";

    useEffect(() => {
        if (token && !authIsFound) {
            const jsonData: any = jwtDecode(token);
            const id = jsonData["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"];
            const roles = jsonData["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
            const userName = jsonData["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];
            const givenname = jsonData["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"];
            const emailAddress = jsonData["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"];
            setAuth({ id, userName, givenname, emailAddress, roles, accessToken: token, profileImage: profileImg });
            setAuthIsFound(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const clickedTarget = event.target as Node;

            if (dropDownVisibleRef.current && !dropDownVisibleRef.current.contains(clickedTarget)) {
                setHideMenuDropDown(false);
            }
        }

        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    if (hideHeaderForBookingConfirm.endsWith(location.pathname)
        || location.pathname?.startsWith(hideHeaderForEmailView)
    ) {
        return <></>;
    }

    const linkClasses = ({ isActive }: { isActive: boolean }) =>
        `text-sm hover:text-primary ${isActive ? "text-primary font-semibold" : "text-gray-500"
        }`;

    const logOut = () => {
        setHideMenuDropDown(false);
        setAuth(null);
        Cookies.remove("token-express-tire");
        Cookies.remove("role-express-tire");
        Cookies.remove("userId-express-tire");
        Cookies.remove("userName-express-tire");
        Cookies.remove("fullName-express-tire");
        Cookies.remove("roleId-express-tire");
        Cookies.remove("profileImg-express-tire");
        Cookies.remove("expires-express-tire");
        navigate("/login");
    }

    const normalize = (value: string) =>
        value.replace(/\s+/g, "").replace(/-/g, "").toLowerCase();
    const hasAnyAdminSettingsPermission = appAdminSettingRoutes.some((item) => {
        if (isAdmin) return true;

        const parentKey = normalize(item.section);
        if (permissions?.[parentKey]) return true;

        return item.child?.some((child: any) => {
            const childKey = normalize(child.section);
            return permissions?.[childKey] === true;
        });
    });

    const firstAllowedAdminSetting = appAdminSettingRoutes
        .flatMap((item) => item.child || [])
        .find((child) => {
            if (isAdmin) return true;

            const key = normalize(child.section);
            return permissions?.[key] === true;
        });

    const settingsNavigationLink = isAdmin
        ? "/admin/settings/users"
        : firstAllowedAdminSetting?.path || null;

    const showSettingsIcon = isAdmin || hasAnyAdminSettingsPermission;


    return (
        <>
            <nav className="sticky top-0 z-[9999] bg-white shadow-sm">
                <TopBar />
                {
                    (!location.pathname.replace("/", "").startsWith(hideHeaderForAdmin)) && (
                        <div className="w-full px-3 flex items-center justify-between">
                            <div>
                                <NavLink to="/">
                                    <img className="logo-img w-[140px] h-[90px]" src="/images/logo_backgroundless.png" alt="logo" />
                                </NavLink>
                            </div>
                            <div className="flex items-center gap-6">
                                <NavLink
                                    to="/"
                                    className={linkClasses}
                                >
                                    Home
                                </NavLink>
                                <NavLink
                                    to="/about-us"
                                    className={linkClasses}
                                >
                                    About Us
                                </NavLink>
                                <NavLink
                                    to="/features"
                                    className={linkClasses}
                                >
                                    Features
                                </NavLink>

                                <NavLink
                                    to="/faq"
                                    className={linkClasses}
                                >
                                    FAQ
                                </NavLink>

                                <NavLink
                                    to="/blogs"
                                    className={linkClasses}
                                >
                                    Blogs
                                </NavLink>

                                <NavLink
                                    to="/locations"
                                    className={linkClasses}
                                >
                                    Locations
                                </NavLink>

                                <NavLink
                                    to="/contact-us"
                                    className={linkClasses}
                                >
                                    Contact Us
                                </NavLink>
                            </div>
                        </div>
                    )
                }

                {
                    (location.pathname.replace("/", "").startsWith(hideHeaderForAdmin)) && (
                        <div className="w-full px-3 flex items-center justify-between">
                            <div>
                                <NavLink to="/">
                                    <img className="logo-img w-[140px] h-[90px]" src="/images/logo_backgroundless.png" alt="logo" />
                                </NavLink>
                            </div>
                            {
                                token
                                    ? (
                                        <div ref={dropDownVisibleRef} className="flex items-center gap-4 relative">
                                            {showSettingsIcon && settingsNavigationLink && (
                                                <button
                                                    onClick={() => navigate(settingsNavigationLink)}
                                                    className="mx-7"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="16px"
                                                        height="16px"
                                                        className="cursor-pointer hover:fill-tertiary"
                                                        viewBox="0 0 478.703 478.703"
                                                    >
                                                        <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                                        <g
                                                            id="SVGRepo_tracerCarrier"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        ></g>
                                                        <g id="SVGRepo_iconCarrier">
                                                            {" "}
                                                            <g>
                                                                {" "}
                                                                <g>
                                                                    <path d="M454.2,189.101l-33.6-5.7c-3.5-11.3-8-22.2-13.5-32.6l19.8-27.7c8.4-11.8,7.1-27.9-3.2-38.1l-29.8-29.8 c-5.6-5.6-13-8.7-20.9-8.7c-6.2,0-12.1,1.9-17.1,5.5l-27.8,19.8c-10.8-5.7-22.1-10.4-33.8-13.9l-5.6-33.2 c-2.4-14.3-14.7-24.7-29.2-24.7h-42.1c-14.5,0-26.8,10.4-29.2,24.7l-5.8,34c-11.2,3.5-22.1,8.1-32.5,13.7l-27.5-19.8 c-5-3.6-11-5.5-17.2-5.5c-7.9,0-15.4,3.1-20.9,8.7l-29.9,29.8c-10.2,10.2-11.6,26.3-3.2,38.1l20,28.1 c-5.5,10.5-9.9,21.4-13.3,32.7l-33.2,5.6c-14.3,2.4-24.7,14.7-24.7,29.2v42.1c0,14.5,10.4,26.8,24.7,29.2l34,5.8 c3.5,11.2,8.1,22.1,13.7,32.5l-19.7,27.4c-8.4,11.8-7.1,27.9,3.2,38.1l29.8,29.8c5.6,5.6,13,8.7,20.9,8.7c6.2,0,12.1-1.9,17.1-5.5 l28.1-20c10.1,5.3,20.7,9.6,31.6,13l5.6,33.6c2.4,14.3,14.7,24.7,29.2,24.7h42.2c14.5,0,26.8-10.4,29.2-24.7l5.7-33.6 c11.3-3.5,22.2-8,32.6-13.5l27.7,19.8c5,3.6,11,5.5,17.2,5.5l0,0c7.9,0,15.3-3.1,20.9-8.7l29.8-29.8c10.2-10.2,11.6-26.3,3.2-38.1 l-19.8-27.8c5.5-10.5,10.1-21.4,13.5-32.6l33.6-5.6c14.3-2.4,24.7-14.7,24.7-29.2v-42.1 C478.9,203.801,468.5,191.501,454.2,189.101z M451.9,260.401c0,1.3-0.9,2.4-2.2,2.6l-42,7c-5.3,0.9-9.5,4.8-10.8,9.9 c-3.8,14.7-9.6,28.8-17.4,41.9c-2.7,4.6-2.5,10.3,0.6,14.7l24.7,34.8c0.7,1,0.6,2.5-0.3,3.4l-29.8,29.8c-0.7,0.7-1.4,0.8-1.9,0.8 c-0.6,0-1.1-0.2-1.5-0.5l-34.7-24.7c-4.3-3.1-10.1-3.3-14.7-0.6c-13.1,7.8-27.2,13.6-41.9,17.4c-5.2,1.3-9.1,5.6-9.9,10.8l-7.1,42 c-0.2,1.3-1.3,2.2-2.6,2.2h-42.1c-1.3,0-2.4-0.9-2.6-2.2l-7-42c-0.9-5.3-4.8-9.5-9.9-10.8c-14.3-3.7-28.1-9.4-41-16.8 c-2.1-1.2-4.5-1.8-6.8-1.8c-2.7,0-5.5,0.8-7.8,2.5l-35,24.9c-0.5,0.3-1,0.5-1.5,0.5c-0.4,0-1.2-0.1-1.9-0.8l-29.8-29.8 c-0.9-0.9-1-2.3-0.3-3.4l24.6-34.5c3.1-4.4,3.3-10.2,0.6-14.8c-7.8-13-13.8-27.1-17.6-41.8c-1.4-5.1-5.6-9-10.8-9.9l-42.3-7.2 c-1.3-0.2-2.2-1.3-2.2-2.6v-42.1c0-1.3,0.9-2.4,2.2-2.6l41.7-7c5.3-0.9,9.6-4.8,10.9-10c3.7-14.7,9.4-28.9,17.1-42 c2.7-4.6,2.4-10.3-0.7-14.6l-24.9-35c-0.7-1-0.6-2.5,0.3-3.4l29.8-29.8c0.7-0.7,1.4-0.8,1.9-0.8c0.6,0,1.1,0.2,1.5,0.5l34.5,24.6 c4.4,3.1,10.2,3.3,14.8,0.6c13-7.8,27.1-13.8,41.8-17.6c5.1-1.4,9-5.6,9.9-10.8l7.2-42.3c0.2-1.3,1.3-2.2,2.6-2.2h42.1 c1.3,0,2.4,0.9,2.6,2.2l7,41.7c0.9,5.3,4.8,9.6,10,10.9c15.1,3.8,29.5,9.7,42.9,17.6c4.6,2.7,10.3,2.5,14.7-0.6l34.5-24.8 c0.5-0.3,1-0.5,1.5-0.5c0.4,0,1.2,0.1,1.9,0.8l29.8,29.8c0.9,0.9,1,2.3,0.3,3.4l-24.7,34.7c-3.1,4.3-3.3,10.1-0.6,14.7 c7.8,13.1,13.6,27.2,17.4,41.9c1.3,5.2,5.6,9.1,10.8,9.9l42,7.1c1.3,0.2,2.2,1.3,2.2,2.6v42.1H451.9z"></path>{" "}
                                                                    <path d="M239.4,136.001c-57,0-103.3,46.3-103.3,103.3s46.3,103.3,103.3,103.3s103.3-46.3,103.3-103.3S296.4,136.001,239.4,136.001 z M239.4,315.601c-42.1,0-76.3-34.2-76.3-76.3s34.2-76.3,76.3-76.3s76.3,34.2,76.3,76.3S281.5,315.601,239.4,315.601z"></path>
                                                                </g>{" "}
                                                            </g>{" "}
                                                        </g>
                                                    </svg>
                                                </button>
                                            )}

                                            <div
                                                className="flex gap-1.5 cursor-pointer"
                                                onClick={() => setHideMenuDropDown(!hideMenuDropDown)}
                                            >
                                                {auth?.profileImage !== "" && auth?.profileImage !== "null" && auth?.profileImage !== "string" ? (

                                                    <img
                                                        className="w-[40px] h-[40px] rounded-full shadow-md"
                                                        alt="profileImg"
                                                        src={`${baseURL}commonFile/Get?name=${auth?.profileImage}`}
                                                        onError={({ currentTarget }) => {
                                                            currentTarget.onerror = null;
                                                            currentTarget.src = "./images/profile-placeholder.png";
                                                        }}
                                                    />

                                                ) : (

                                                    <img src="./images/profile-placeholder.png" alt="profile_placeholder" className="w-[40px] h-[40px] rounded-full shadow-md" />

                                                )}

                                                <button
                                                    className="text-primary p-2 rounded-[20px] text-sm flex items-center gap-x-[0.75rem]"

                                                >
                                                    <span className="font-medium">{auth?.givenname}</span>
                                                    <span className="material-symbols-outlined text-lg">
                                                        {
                                                            hideMenuDropDown ? (
                                                                "keyboard_arrow_up"
                                                            )
                                                                :
                                                                (
                                                                    "keyboard_arrow_down"
                                                                )
                                                        }
                                                    </span>
                                                </button>
                                            </div>

                                            {hideMenuDropDown && (
                                                <div className="absolute right-0 w-44 bg-white shadow-lg border rounded-md top-[50px] ml-20">
                                                    <ul className="text-sm text-gray-700">


                                                        <li
                                                            className="px-4 py-2 hover:bg-gray-100 hover:underline cursor-pointer"
                                                            onClick={logOut}
                                                        >
                                                            Logout
                                                        </li>
                                                    </ul>
                                                </div>
                                            )}


                                        </div>
                                    )
                                    : (<></>)
                            }

                        </div>
                    )
                }

            </nav>
        </>
    )

}